/* Unified flow layer: transparently uses the network when online and the
 * cached protocol library + outbox when offline. Components call these instead
 * of hitting axios directly, so offline behaviour lives in one place. */
import { http } from "@/lib/api";
import * as off from "@/lib/offline";
import { checkinAttribution } from "@/lib/attribution";

export async function submitCheckin({ deviceId, input, ctx, time, content, reassessOf }) {
  try {
    const { data } = await http.post("/checkin", {
      device_id: deviceId,
      internal_condition_input: input.id,
      demand_context: ctx.id,
      available_time: time.id,
      reassess_of: reassessOf || null,
      ...checkinAttribution(),
    });
    return data;
  } catch (e) {
    const lib = off.getLib();
    if (!lib) throw e; // no cached library — can't build a result offline
    const protocol = off.selectLocal(input.mode, ctx.id, time.id, lib);
    const result = off.buildResultLocal(input.mode, ctx, protocol, lib);
    off.enqueue({
      id: result.checkin_id,
      device_id: deviceId,
      internal_condition_input: input.id,
      demand_context: ctx.id,
      available_time: time.id,
      response_mode: input.mode,
      protocol_id: protocol.id,
      is_baseline: false,
      accuracy_rating: null,
      feedback: null,
      created_at: new Date().toISOString(),
    });
    return result;
  }
}

export async function postAccuracy(result, rating) {
  if (result._offline) {
    off.updateRecord(result.checkin_id, { accuracy_rating: rating });
    return { corrections: null };
  }
  const { data } = await http.post(`/checkin/${result.checkin_id}/accuracy`, { rating });
  return data;
}

export async function correctMode(result, mode, ctxObj) {
  if (result._offline) {
    const lib = off.getLib();
    const protocol = off.selectLocal(mode, result._ctxId || (ctxObj && ctxObj.id), result._timeId, lib);
    const next = off.buildResultLocal(mode, ctxObj, protocol, lib);
    next.checkin_id = result.checkin_id; // keep same record
    next._ctxId = result._ctxId; next._timeId = result._timeId;
    off.updateRecord(result.checkin_id, { response_mode: mode, protocol_id: protocol.id });
    return next;
  }
  const { data } = await http.post(`/checkin/${result.checkin_id}/correct`, { mode });
  return data;
}

export async function getSmaller(result) {
  if (result._offline) {
    const lib = off.getLib();
    const cur = result.protocol;
    const protocol = off.selectLocal(result.mode, result._ctxId, result._timeId, lib, {
      excludeIds: [cur.id],
      maxDuration: cur.duration_seconds > 60 ? cur.duration_seconds : null,
    });
    off.updateRecord(result.checkin_id, { protocol_id: protocol.id });
    return { protocol };
  }
  const { data } = await http.get(`/checkin/${result.checkin_id}/smaller`);
  return data;
}

export async function submitFeedback(result, feedback) {
  if (result._offline) {
    off.updateRecord(result.checkin_id, { feedback, response_mode: result.mode, protocol_id: result.protocol.id });
    return { ok: true, offline: true };
  }
  const { data } = await http.post(`/checkin/${result.checkin_id}/feedback`, feedback);
  return data;
}

export async function saveOperatingState(result, payload) {
  if (result._offline) {
    off.updateRecord(result.checkin_id, { operating_state: payload });
    return { ok: true, offline: true };
  }
  const { data } = await http.post(`/checkin/${result.checkin_id}/operating-state`, payload);
  return data;
}

export async function getSupportText(result, audienceId, { save, savedText, channel } = {}) {
  if (result._offline) {
    const lib = off.getLib();
    return { text: off.supportTextLocal(lib, result.mode, audienceId) };
  }
  const { data } = await http.post(`/checkin/${result.checkin_id}/support-signal`, {
    audience_type: audienceId, save: !!save, saved_text: savedText, shared_or_copied: channel,
  });
  return data;
}
